import { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FiCheckCircle, FiSmartphone, FiShield,
  FiMenu, FiX, FiArrowRight,
  FiClipboard, FiHome, FiBriefcase,
  FiList, FiUser, FiClock, FiRefreshCw,
  FiAlertCircle, FiBarChart2, FiHeart
} from 'react-icons/fi';
import { USER_ROLES } from '../utils/constants';
import CookieConsent from '../components/common/CookieConsent';
import SubscriptionModal from '../components/subscription/SubscriptionModal';
import { subscriptionsAPI } from '../services/api';
import axios from 'axios';

const LandingPage = () => {
  const { isAuthenticated, user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [planFeatures, setPlanFeatures] = useState({});
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState(null);
  const [trialDays, setTrialDays] = useState(7);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  // Navbar scroll effect
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typewriter effect for hero
  const heroWords = ['Organiza', 'Simplifica', 'Digitaliza', 'Automatiza', 'Optimiza'];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = heroWords[currentWordIndex];
    const typeSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;

    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), pauseTime);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentWord.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % heroWords.length);
        }
      }
    }, typeSpeed);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex]);

  // Load pricing plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const plans = await subscriptionsAPI.getPlans();

        const featuresPromises = plans.map(async (plan) => {
          try {
            const planFeaturesData = await subscriptionsAPI.getPlanFeatures(plan.id);
            return { planId: plan.id, features: planFeaturesData.features };
          } catch (error) {
            console.error(`Error loading features for plan ${plan.id}:`, error);
            return {
              planId: plan.id,
              features: {
                incident_management: true,
                custom_cleaning_types: true,
                custom_bed_dispositions: true
              }
            };
          }
        });

        const allFeatures = await Promise.all(featuresPromises);
        const featuresMap = {};
        allFeatures.forEach(({ planId, features }) => {
          featuresMap[planId] = features;
        });
        setPlanFeatures(featuresMap);

        const transformedPlans = plans.map(plan => {
          const planFeaturesData = featuresMap[plan.id] || {
            incident_management: true,
            custom_cleaning_types: true,
            custom_bed_dispositions: true
          };

          const features = [
            { name: plan.max_rooms ? `Hasta ${plan.max_rooms} habitaciones` : 'Habitaciones ilimitadas', included: true },
            { name: plan.max_common_spaces ? `Hasta ${plan.max_common_spaces} espacios comunes` : 'Espacios comunes ilimitados', included: true },
            { name: plan.max_users ? `Hasta ${plan.max_users} usuarios` : 'Usuarios ilimitados', included: true },
            { name: 'App móvil incluida', included: true },
            { name: 'Registro de jornada', included: true },
            { name: 'Cuadrantes y planificación', included: true },
            { name: 'Reportes y analytics', included: true },
            { name: 'Gestión de costes del personal', included: true },
            { name: 'Gestión de incidencias', included: planFeaturesData.incident_management },
            { name: 'Tipos de limpieza personalizados', included: planFeaturesData.custom_cleaning_types },
            {
              name: plan.plan === 'basic' ? 'Soporte por email' :
                    plan.plan === 'professional' ? 'Soporte prioritario' :
                    'Soporte 24/7',
              included: true
            },
            { name: 'Sin permanencia', included: true }
          ];

          return {
            id: plan.id,
            plan: plan.plan,
            name: plan.name,
            monthlyPrice: parseFloat(plan.monthly_price),
            monthlyPriceOriginal: plan.monthly_price_original ? parseFloat(plan.monthly_price_original) : null,
            yearlyPrice: parseFloat(plan.annual_price),
            yearlyPriceOriginal: plan.annual_price_original ? parseFloat(plan.annual_price_original) : null,
            annualDiscountPercentage: plan.annual_discount_percentage,
            pricesIncludeIVA: plan.prices_include_iva || false,
            ivaPercentage: plan.iva_percentage || 21,
            description: plan.description || '',
            subtitle: plan.max_rooms || plan.max_common_spaces
              ? [
                  plan.max_rooms ? `${plan.max_rooms} hab.` : null,
                  plan.max_common_spaces ? `${plan.max_common_spaces} esp. comunes` : null
                ].filter(Boolean).join(' · ')
              : 'Capacidad ilimitada',
            features,
            highlighted: plan.plan === 'professional',
            isCustom: false
          };
        });

        setPricingPlans(transformedPlans);

        const maxTrialDays = Math.max(...plans.map(p => p.trial_days || 0), 7);
        setTrialDays(maxTrialDays);
      } catch (error) {
        console.error('Error loading plans:', error);
        setPricingPlans([
          {
            name: 'Básico',
            monthlyPrice: 9,
            yearlyPrice: 86,
            description: 'Ideal para hostales y pequeños B&B',
            subtitle: '20 hab. · 5 esp. comunes',
            features: [
              { name: 'Hasta 20 habitaciones', included: true },
              { name: 'Hasta 5 espacios comunes', included: true },
              { name: 'Hasta 5 usuarios', included: true },
              { name: 'App móvil incluida', included: true },
              { name: 'Registro de jornada', included: true },
              { name: 'Cuadrantes y planificación', included: true },
              { name: 'Reportes y analytics', included: true },
              { name: 'Gestión de costes del personal', included: true },
              { name: 'Gestión de incidencias', included: true },
              { name: 'Tipos de limpieza personalizados', included: false },
              { name: 'Soporte por email', included: true },
              { name: 'Sin permanencia', included: true }
            ],
            highlighted: false
          },
          {
            name: 'Profesional',
            monthlyPrice: 29,
            yearlyPrice: 279,
            description: 'Perfecto para hoteles boutique',
            subtitle: '100 hab. · 20 esp. comunes',
            features: [
              { name: 'Hasta 100 habitaciones', included: true },
              { name: 'Hasta 20 espacios comunes', included: true },
              { name: 'Usuarios ilimitados', included: true },
              { name: 'App móvil incluida', included: true },
              { name: 'Registro de jornada', included: true },
              { name: 'Cuadrantes y planificación', included: true },
              { name: 'Reportes y analytics', included: true },
              { name: 'Gestión de costes del personal', included: true },
              { name: 'Gestión de incidencias', included: true },
              { name: 'Tipos de limpieza personalizados', included: true },
              { name: 'Soporte prioritario', included: true },
              { name: 'Sin permanencia', included: true }
            ],
            highlighted: true
          },
          {
            name: 'Empresarial',
            monthlyPrice: null,
            yearlyPrice: null,
            description: 'Para cadenas y grupos hoteleros',
            subtitle: 'Capacidad ilimitada',
            features: [
              { name: 'Habitaciones ilimitadas', included: true },
              { name: 'Espacios comunes ilimitados', included: true },
              { name: 'Usuarios ilimitados', included: true },
              { name: 'App móvil incluida', included: true },
              { name: 'Registro de jornada', included: true },
              { name: 'Cuadrantes y planificación', included: true },
              { name: 'Reportes y analytics avanzados', included: true },
              { name: 'Gestión de costes del personal', included: true },
              { name: 'Gestión de incidencias', included: true },
              { name: 'Tipos de limpieza personalizados', included: true },
              { name: 'Múltiples establecimientos', included: true },
              { name: 'Soporte 24/7', included: true },
              { name: 'Gestor de cuenta dedicado', included: true },
              { name: 'Integración con tu PMS', included: true },
              { name: 'Formación personalizada', included: true },
              { name: 'Sin permanencia', included: true }
            ],
            highlighted: false,
            isCustom: true
          }
        ]);
      } finally {
        setLoadingPlans(false);
      }
    };

    fetchPlans();
  }, []);

  // Redirect authenticated users to their appropriate dashboard
  if (isAuthenticated) {
    if (user?.role === USER_ROLES.CLEANING_STAFF) {
      return <Navigate to="/orders/mobile" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const formatPrice = (price) => {
    if (Number.isInteger(price)) {
      return price.toString();
    }
    return price.toFixed(2).replace('.', ',');
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.teonila.com';

      await axios.post(`${apiUrl}/api/v1/contact/demo-request`, formData);

      // Track conversion events
      if (typeof gtag === 'function') {
        gtag('event', 'generate_lead', {
          event_category: 'contact',
          event_label: formData.company || 'unknown',
          value: 1
        });
      }

      alert('¡Gracias! Tu solicitud ha sido enviada correctamente. Nos pondremos en contacto contigo pronto.');
      setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error sending demo request:', error);
      alert('Hubo un error al enviar tu solicitud. Por favor, inténtalo de nuevo o contacta directamente a info@teonila.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubscribeClick = () => {
    scrollToSection('contacto');
  };

  // Beneficios por tipo de usuario
  const userBenefits = [
    {
      icon: <FiHome className="w-12 h-12" />,
      title: 'Personal de Limpieza',
      color: 'from-emerald-500 to-teal-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      image: 'https://images.pexels.com/photos/6466478/pexels-photo-6466478.jpeg?auto=compress&cs=tinysrgb&w=800',
      imageAlt: 'Camarera de pisos arreglando almohadas en habitación de hotel',
      benefits: [
        'Ve sus tareas asignadas en el móvil',
        'Marca habitaciones como limpias con un toque',
        'Reporta incidencias con fotos',
        'Ficha entrada y salida fácilmente',
        'Sabe exactamente qué habitaciones son prioritarias'
      ]
    },
    {
      icon: <FiClipboard className="w-12 h-12" />,
      title: 'Personal de Recepción',
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      image: 'https://images.pexels.com/photos/3771110/pexels-photo-3771110.jpeg?auto=compress&cs=tinysrgb&w=800',
      imageAlt: 'Mostrador de recepción de hotel con campana de servicio',
      benefits: [
        'Estado de habitaciones en tiempo real',
        'Marca salidas y asigna prioridades',
        'Comunica con limpieza sin llamadas',
        'Check-ins más rápidos y eficientes',
        'Gestiona incidencias al instante'
      ]
    },
    {
      icon: <FiBriefcase className="w-12 h-12" />,
      title: 'Administración',
      color: 'from-purple-500 to-violet-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
      imageAlt: 'Panel de administración y analytics en pantalla de ordenador',
      benefits: [
        'Control total de costes de personal',
        'Reportes de productividad detallados',
        'Gestión de cuadrantes y turnos',
        'Historial completo de actividad',
        'Decisiones basadas en datos reales'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar - fixed, transparent on hero, white on scroll */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm'
          : 'bg-transparent border-b border-transparent'
      }`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-6">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <div className="flex-shrink-0">
                <span className="text-2xl font-bold text-gray-900">Teonila</span>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-8">
                <button
                  onClick={() => scrollToSection('funcionalidades')}
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Funcionalidades
                </button>
                <button
                  onClick={() => scrollToSection('beneficios')}
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Beneficios
                </button>
                <button
                  onClick={() => scrollToSection('precios')}
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Precios
                </button>
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Contacto
                </button>
              </div>

              {/* Desktop CTA buttons */}
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-5 py-2.5 rounded-full border border-gray-300 text-gray-700 font-medium hover:border-gray-400 hover:bg-white/50 transition-all"
                >
                  Iniciar sesión
                </Link>
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="px-5 py-2.5 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all"
                >
                  Pruébalo gratis
                </button>
              </div>

              {/* Mobile menu button */}
              <div className="lg:hidden flex items-center gap-3">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="px-4 py-2 text-sm rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all"
                >
                  Pruébalo gratis
                </button>
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-700 hover:text-blue-600 p-2"
                >
                  {mobileMenuOpen ? <FiX className="w-6 h-6" /> : <FiMenu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden bg-white/95 backdrop-blur-sm border-t shadow-lg">
              <div className="px-4 pt-2 pb-4 space-y-1">
                <button onClick={() => scrollToSection('funcionalidades')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium">
                  Funcionalidades
                </button>
                <button onClick={() => scrollToSection('beneficios')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium">
                  Beneficios
                </button>
                <button onClick={() => scrollToSection('precios')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium">
                  Precios
                </button>
                <button onClick={() => scrollToSection('contacto')} className="block w-full text-left px-4 py-3 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg font-medium">
                  Contacto
                </button>
                <div className="pt-3 border-t border-gray-100">
                  <Link to="/login" className="block w-full text-center px-4 py-3 text-blue-600 font-medium">
                    Iniciar sesión
                  </Link>
                </div>
              </div>
            </div>
          )}
        </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen pt-20 overflow-hidden flex items-center" style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 50%, #f0f4ff 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 relative w-full py-8 lg:py-0">

            {/* Text content */}
            <div className="w-full lg:w-[55%] text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                <span className="text-blue-600 italic inline-block min-w-[180px] sm:min-w-[220px] lg:min-w-[280px]">
                  {displayText}
                  <span className="animate-pulse">|</span>
                </span>
                <span className="block">la limpieza de tu alojamiento</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed max-w-lg mx-auto lg:mx-0">
                Gestión de housekeeping sencilla, rápida y sin complicaciones para tu hotel, hostal o apartamentos turísticos.
              </p>

              {/* Feature bullets */}
              <ul className="space-y-3 mb-10 text-left max-w-md mx-auto lg:mx-0">
                {[
                  'Asignación de habitaciones en segundos',
                  'Estado de limpiezas en tiempo real',
                  'Control de jornadas y fichaje digital',
                  'App móvil para tu equipo',
                  'Sin permanencia ni compromisos'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-blue-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-gray-700 text-sm sm:text-base">{item}</span>
                  </li>
                ))}
              </ul>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 sm:gap-4">
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="px-6 py-2.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                >
                  Pruébalo Gratis
                </button>
                <button
                  onClick={() => scrollToSection('contacto')}
                  className="px-6 py-2.5 rounded-full border border-gray-300 bg-white/80 text-gray-700 font-medium hover:bg-white hover:border-gray-400 transition-all whitespace-nowrap"
                >
                  Contacta Ventas
                </button>
              </div>
            </div>

            {/* Mobile mockup - visible on mobile/tablet, absolute on desktop */}
            <div className="mt-12 flex justify-center lg:hidden">
              <div className="h-[50vh] sm:h-[55vh] aspect-[9/19.5]">
                <div className="bg-gray-800 rounded-[2rem] p-1.5 shadow-2xl h-full w-full">
                  <div className="rounded-[1.5rem] overflow-hidden h-full w-full">
                    <img
                      src="/images/landing/mobile-screenshot.jpg"
                      alt="App móvil Teonila - lista de órdenes de limpieza"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile mockup - desktop only, positioned absolutely */}
            <div className="hidden lg:block absolute bottom-[-12%] right-[25%] z-10">
              <div className="h-[55vh] aspect-[9/19.5]">
                <div className="bg-gray-800 rounded-[2rem] p-1.5 shadow-2xl h-full w-full">
                  <div className="rounded-[1.5rem] overflow-hidden h-full w-full">
                    <img
                      src="/images/landing/mobile-screenshot.jpg"
                      alt="App móvil Teonila - lista de órdenes de limpieza"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop mockup - desktop only, positioned absolutely */}
            <div className="hidden lg:block absolute bottom-[-5%] right-[-30%]">
              <div className="h-[60vh] aspect-[1447/967]">
                <div className="rounded-xl shadow-2xl overflow-hidden border border-gray-200 bg-white h-full flex flex-col">
                  {/* Browser chrome */}
                  <div className="bg-gray-100 px-4 py-2.5 flex items-center gap-3 border-b border-gray-200 flex-shrink-0">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                    <div className="flex-1 flex justify-center">
                      <div className="bg-white rounded-md px-4 py-1 text-xs text-gray-500 border border-gray-200">
                        teonila.com/dashboard
                      </div>
                    </div>
                  </div>
                  {/* Screenshot */}
                  <div className="flex-1 overflow-hidden">
                    <img
                      src="/images/landing/desktop-screenshot.png"
                      alt="Panel de control Teonila - gestión de habitaciones"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

        </div>
      </section>

      {/* SEO Features Section - Software de Housekeeping */}
      <section id="funcionalidades" className="py-20 px-4 sm:px-4 lg:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Funcionalidades
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Todo lo que necesitas para gestionar el housekeeping de tu alojamiento
            </p>
          </div>
          <div className="space-y-24">
            
            {/* Feature 1 - Imagen derecha */}
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center">
                    <FiList className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Órdenes de limpieza organizadas y sin confusiones
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  La gestión diaria del <strong>departamento de pisos</strong> deja de ser un rompecabezas. 
                  Desde Teonila, la <strong>gobernanta</strong> puede asignar habitaciones a cada <strong>camarera de pisos</strong> en 
                  cuestión de segundos, con la seguridad de que verán las tareas en su móvil al instante. 
                  Todo queda centralizado en un único <strong>software de housekeeping</strong>, evitando papeles, 
                  mensajes dispersos o errores que hacen perder tiempo. La operativa diaria se vuelve más fluida y transparente.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1556741533-411cf82e4e2d?auto=format&fit=crop&w=800&q=80"
                    alt="Gobernanta organizando tareas de limpieza en tablet"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 2 - Imagen izquierda */}
            <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center">
                    <FiUser className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Camareras que saben exactamente qué hacer
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Cada <strong>camarera de pisos</strong> accede desde la <strong>app móvil</strong> a un listado 
                  claro con sus habitaciones del día, el <strong>tipo de limpieza</strong> que debe realizar 
                  (salida, cliente o repaso) y el estado actualizado en tiempo real. Con un simple toque pueden 
                  marcar cuando finalizan. Menos dudas, menos interrupciones y más eficiencia en cada turno.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.pexels.com/photos/6466478/pexels-photo-6466478.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Camarera de pisos arreglando habitación de hotel"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 3 - Imagen derecha */}
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-purple-100 flex items-center justify-center">
                    <FiClock className="w-7 h-7 text-purple-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Fichaje digital que cumple con la ley
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  El <strong>registro de jornada</strong> se vuelve sencillo para todos. Cada empleado <strong>ficha 
                  desde su móvil</strong> usando <strong>geolocalización</strong> y puede consultar sus horas trabajadas, 
                  turnos y cuadrantes sin tener que preguntar. Para la empresa, esto significa <strong>control horario</strong> real, 
                  orden y la tranquilidad de cumplir con la normativa vigente, con todo documentado de forma fiable y accesible.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?auto=format&fit=crop&w=800&q=80"
                    alt="Empleado fichando desde su móvil"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 4 - Imagen izquierda */}
            <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <FiRefreshCw className="w-7 h-7 text-orange-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Cambios en tiempo real desde recepción
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Sabemos que en un hotel todo puede cambiar en un minuto. Por eso Teonila permite modificar 
                  prioridades, reasignar habitaciones o añadir tareas nuevas <strong>en tiempo real</strong>. El equipo 
                  de pisos recibe las actualizaciones inmediatamente mediante <strong>notificaciones push</strong>, sin 
                  necesidad de llamadas ni mensajes. La <strong>comunicación entre recepción y pisos</strong> se vuelve 
                  instantánea y sin fricciones.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.pexels.com/photos/3771110/pexels-photo-3771110.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Recepción de hotel gestionando tareas"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 5 - Imagen derecha */}
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center">
                    <FiAlertCircle className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Incidencias registradas al instante
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Cuando una camarera encuentra una <strong>avería</strong>, una falta de amenities o cualquier <strong>incidencia</strong>, 
                  puede reportarlo desde la propia habitación con una foto y un comentario. Recepción y <strong>mantenimiento</strong> reciben 
                  la alerta al momento, lo que permite actuar rápido, dar seguimiento y evitar que un problema afecte a otro huésped. 
                  Todo queda registrado en el historial.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80"
                    alt="Técnico de mantenimiento revisando instalaciones"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 6 - Imagen izquierda */}
            <div className="flex flex-col lg:flex-row-reverse gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
                    <FiBarChart2 className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Estadísticas para mejorar la operativa
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Teonila genera <strong>reportes de productividad</strong> con tiempos de limpieza, cargas de trabajo, 
                  habitaciones por persona, historial de incidencias y más. Estos <strong>datos y analytics</strong> permiten 
                  al administrador optimizar turnos, equilibrar tareas y detectar oportunidades de mejora. 
                  Decisiones basadas en <strong>datos reales</strong>, no en intuiciones.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                    alt="Dashboard con analytics y reportes de productividad"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

            {/* Feature 7 - Imagen derecha */}
            <div className="flex flex-col lg:flex-row gap-12 items-center">
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-pink-100 flex items-center justify-center">
                    <FiHeart className="w-7 h-7 text-pink-600" />
                  </div>
                  <h3 className="text-2xl lg:text-3xl font-bold text-gray-900">
                    Pensada para hoteles pequeños y medianos
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed text-lg">
                  Teonila nace de la experiencia directa en alojamientos familiares, donde cada minuto y cada recurso cuentan. 
                  Está diseñada para <strong>hoteles boutique, hostales, pensiones y casas rurales</strong> que necesitan 
                  organización profesional sin sistemas complejos ni contratos de permanencia. Fácil, intuitiva y con precios justos.
                </p>
              </div>
              <div className="flex-1 w-full">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80"
                    alt="Hotel boutique pequeño y acogedor"
                    loading="lazy"
                    className="w-full h-72 lg:h-80 object-cover"
                  />
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Benefits by User Type Section */}
      <section id="beneficios" className="py-24 px-4 sm:px-4 lg:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Beneficios para cada rol
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Teonila está diseñada para facilitar el trabajo de todos los miembros de tu equipo
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {userBenefits.map((card, index) => (
              <div
                key={index}
                className={`relative rounded-3xl overflow-hidden ${card.bgColor} border-2 ${card.borderColor} hover:shadow-xl transition-all duration-300 animate-scale-in`}
                style={{ animationDelay: `${index * 0.15}s` }}
              >
                {/* Card image */}
                <div className="w-full" style={{ aspectRatio: '16/9' }}>
                  <img
                    src={card.image}
                    alt={card.imageAlt}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${card.color} flex items-center justify-center text-white mb-6 shadow-lg -mt-16 relative z-10 border-4 border-white`}>
                    {card.icon}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {card.title}
                  </h3>

                  {/* Benefits list */}
                  <ul className="space-y-4">
                    {card.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <FiCheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          index === 0 ? 'text-emerald-500' :
                          index === 1 ? 'text-blue-500' :
                          'text-purple-500'
                        }`} />
                        <span className="text-gray-700">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precios" className="py-24 px-4 sm:px-4 lg:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Precios claros y justos
            </h2>
            <p className="text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Paga solo por lo que necesitas. Sin sorpresas, sin permanencia.
            </p>

            {/* Toggle Monthly/Yearly */}
            <div className="inline-flex items-center bg-white rounded-full p-1.5 shadow-md border border-gray-200">
              <button
                onClick={() => setSelectedPlan('monthly')}
                className={`px-6 py-2.5 rounded-full transition-all font-medium ${
                  selectedPlan === 'monthly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Mensual
              </button>
              <button
                onClick={() => setSelectedPlan('yearly')}
                className={`px-6 py-2.5 rounded-full transition-all font-medium flex items-center gap-2 ${
                  selectedPlan === 'yearly'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Anual
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  selectedPlan === 'yearly' ? 'bg-blue-500 text-white' : 'bg-green-100 text-green-700'
                }`}>
                  -20%
                </span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-3xl p-8 flex flex-col animate-scale-in ${
                  plan.highlighted
                    ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-2xl scale-105 z-10'
                    : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-xl'
                }`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-1.5 bg-yellow-400 text-gray-900 rounded-full text-sm font-bold shadow-lg">
                    Más Popular
                  </div>
                )}

                <h3 className={`text-2xl font-bold mb-2 ${
                  plan.highlighted ? 'text-white' : 'text-gray-900'
                }`}>
                  {plan.name}
                </h3>
                <p className={`mb-6 ${
                  plan.highlighted ? 'text-blue-100' : 'text-gray-600'
                }`}>
                  {plan.description}
                </p>

                {plan.isCustom ? (
                  <div className="mb-8">
                    <span className={`text-3xl font-bold ${
                      plan.highlighted ? 'text-white' : 'text-gray-900'
                    }`}>
                      Personalizado
                    </span>
                    <p className={`mt-2 text-sm ${
                      plan.highlighted ? 'text-blue-100' : 'text-gray-600'
                    }`}>
                      Contacta con nosotros
                    </p>
                  </div>
                ) : (
                  <div className="mb-8">
                    {selectedPlan === 'monthly' && plan.monthlyPriceOriginal && (
                      <div className={`text-xl line-through mb-1 ${
                        plan.highlighted ? 'text-blue-200' : 'text-gray-400'
                      }`}>
                        {formatPrice(plan.monthlyPriceOriginal)}€/mes
                      </div>
                    )}
                    {selectedPlan === 'yearly' && plan.yearlyPriceOriginal && (
                      <div className={`text-xl line-through mb-1 ${
                        plan.highlighted ? 'text-blue-200' : 'text-gray-400'
                      }`}>
                        {formatPrice(plan.yearlyPriceOriginal)}€/año
                      </div>
                    )}
                    <div className="flex items-baseline gap-1">
                      <span className="text-5xl font-bold">
                        {formatPrice(selectedPlan === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice)}€
                      </span>
                      <span className={`text-lg ${
                        plan.highlighted ? 'text-blue-100' : 'text-gray-600'
                      }`}>
                        /{selectedPlan === 'monthly' ? 'mes' : 'año'}
                      </span>
                    </div>
                    {selectedPlan === 'yearly' && plan.annualDiscountPercentage && (
                      <div className={`mt-2 text-sm font-semibold ${
                        plan.highlighted ? 'text-yellow-300' : 'text-green-600'
                      }`}>
                        Ahorras {plan.annualDiscountPercentage}% vs mensual
                      </div>
                    )}
                    <p className={`mt-1 text-sm ${
                      plan.highlighted ? 'text-blue-100' : 'text-gray-500'
                    }`}>
                      {plan.pricesIncludeIVA
                        ? `IVA (${plan.ivaPercentage}%) incluido`
                        : `+ IVA (${plan.ivaPercentage}%)`
                      }
                    </p>
                  </div>
                )}

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {feature.included ? (
                        <FiCheckCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          plan.highlighted ? 'text-blue-200' : 'text-green-500'
                        }`} />
                      ) : (
                        <FiX className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                          plan.highlighted ? 'text-blue-300' : 'text-gray-400'
                        }`} />
                      )}
                      <span className={`text-sm ${
                        feature.included
                          ? plan.highlighted ? 'text-blue-50' : 'text-gray-700'
                          : plan.highlighted ? 'text-blue-200 opacity-60 line-through' : 'text-gray-500 line-through'
                      }`}>
                        {feature.name || feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSubscribeClick(plan)}
                  className={`w-full py-4 rounded-full font-semibold transition-all mt-auto ${
                    plan.highlighted
                      ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'
                      : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {plan.isCustom ? 'Contactar' : 'Comenzar Ahora'}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <div className="inline-block bg-blue-50 border border-blue-200 rounded-2xl p-8 max-w-2xl">
              <p className="text-xl font-bold text-blue-900 mb-3">
                {trialDays} Días de Prueba Gratis
              </p>
              <p className="text-blue-700">
                Prueba Teonila sin compromiso durante {trialDays} días. Si cancelas durante el periodo de prueba, te devolvemos el 100% del importe.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          setSelectedPlanForModal(null);
        }}
        plan={selectedPlanForModal}
        period={selectedPlan}
      />

      {/* Contact Section */}
      <section id="contacto" className="py-24 px-4 sm:px-4 lg:px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            {/* Left side - Text content */}
            <div className="lg:sticky lg:top-24">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                ¡Pruébalo gratis!
              </h2>
              <p className="text-xl sm:text-2xl text-gray-700 font-medium mb-8">
                Rellena el formulario y tendrás acceso gratuito durante {trialDays} días a todas las funcionalidades de Teonila.
              </p>
              
              <div className="space-y-6 text-gray-600">
                <p className="leading-relaxed">
                  Nuestro equipo te contactará para explicarte cómo funciona la plataforma y podrás empezar a usarla desde ese momento, <strong className="text-gray-900">sin necesidad de realizar ningún pago</strong>.
                </p>
                <p className="leading-relaxed">
                  Finalizado el periodo de prueba, decidirás si seguir utilizando Teonila activando el plan que mejor se adapte a tus necesidades. <strong className="text-gray-900">No tendrás compromiso de permanencia</strong> y pagarás mensualmente sin sorpresas.
                </p>
              </div>

              {/* Trust points */}
              <div className="mt-10 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FiCheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Sin tarjeta de crédito</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FiCheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Configuración en 5 minutos</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FiCheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">Soporte incluido durante la prueba</span>
                </div>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-200">
              <form onSubmit={handleDemoSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre Completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-gray-900 transition-all"
                    placeholder="Juan Pérez"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-gray-900 transition-all"
                    placeholder="juan@ejemplo.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-gray-900 transition-all"
                    placeholder="+34 600 000 000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del Establecimiento *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-gray-900 transition-all"
                    placeholder="Mi Hotel"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensaje (opcional)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value.slice(0, 1000) })}
                    maxLength={1000}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-gray-900 transition-all resize-none"
                    placeholder="Cuéntanos tus dudas o necesidades..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 rounded-full bg-blue-600 text-white text-lg font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Enviando...'
                  ) : (
                    <>
                      Empezar prueba gratis
                      <FiArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>

                <p className="text-center text-sm text-gray-500">
                  Te responderemos en menos de 24 horas
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 px-4 sm:px-4 lg:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold mb-4">
                Teonila
              </h3>
              <p className="text-gray-400 mb-4 max-w-md">
                Software de gestión de housekeeping diseñado para pequeños y medianos hoteles.
                Simple, potente y sin complicaciones.
              </p>
              <p className="text-gray-500 text-sm">
                ¿Preguntas? <a href="mailto:info@teonila.com" className="text-blue-400 hover:text-blue-300 transition-colors">info@teonila.com</a>
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Producto</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <button onClick={() => scrollToSection('beneficios')} className="hover:text-white transition-colors">
                    Beneficios
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('precios')} className="hover:text-white transition-colors">
                    Precios
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contacto')} className="hover:text-white transition-colors">
                    Contacto
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-4 text-lg">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <Link to="/privacy" className="hover:text-white transition-colors">
                    Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-white transition-colors">
                    Iniciar Sesión
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-500">
            <p>© {new Date().getFullYear()} Teonila. Hecho con cariño para hoteleros.</p>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default LandingPage;
